import { GoogleMap, Marker, useLoadScript, DirectionsRenderer } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "./LiveTracking.css";

function LiveTracking() {
  const locationState = useLocation();
  const orderData = locationState.state?.order;
  const address = locationState.state?.address;
  const city = locationState.state?.city;
 
  const [location, setLocation] = useState({
    lat: "0",
    logs: "0",
  });

  const [riderInfo, setRiderInfo] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [geocodingLoading, setGeocodingLoading] = useState(false);
  const [directions, setDirections] = useState(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyBXZWaGhCKtK8LnNm3eM955g_f0jhB5Dg4",
    libraries: ["places"],
  });

  // Geocode address to get latitude and longitude
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!address && !orderData?.latitude) return;

      setGeocodingLoading(true);
      try {
        // If order has latitude/longitude, use those
        if (orderData?.latitude && orderData?.longitude) {
          setUserLocation({
            lat: parseFloat(orderData.latitude),
            lng: parseFloat(orderData.longitude),
            address: address || orderData.shipping_address,
          });
          setGeocodingLoading(false);
          return;
        }

        // Otherwise geocode the address
        if (address || orderData?.shipping_address) {
          const fullAddress = `${address || orderData.shipping_address}, ${city || orderData.city}`;
          
          const response = await axios.get(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              fullAddress
            )}&key=AIzaSyBXZWaGhCKtK8LnNm3eM955g_f0jhB5Dg4`
          );


          if (response.data.results.length > 0) {
            const { lat, lng } = response.data.results[0].geometry.location;
            setUserLocation({
              lat,
              lng,
              address: fullAddress,
            });
          } else {
            console.warn("No results found for the address");
          }
        }
      } catch (error) {
        console.error("Error geocoding address:", error);
      } finally {
        setGeocodingLoading(false);
      }
    };

    geocodeAddress();
  }, [address, city, orderData]);

  // Get directions from rider to delivery location
  useEffect(() => {
    if (!isLoaded || !userLocation || location.lat === "0") return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: {
          lat: parseFloat(location.lat),
          lng: parseFloat(location.logs),
        },
        destination: {
          lat: userLocation.lat,
          lng: userLocation.lng,
        },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
          
          // Extract distance and duration
          if (result.routes[0]) {
            const leg = result.routes[0].legs[0];
            setDistance(leg.distance.text);
            setDuration(leg.duration.text);
          }
        } else {
          console.error("Directions request failed:", status);
        }
      }
    );
  }, [isLoaded, userLocation, location]);

  useEffect(() => {
    const interval = setInterval(async () => {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}servicemen/location/1`);
      setLocation(res.data.data);
      setRiderInfo(res.data.data);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Bike with rider SVG icon
  const riderMarkerIcon = {
    url: "https://cdn-icons-png.freepik.com/256/3896/3896940.png?semt=ais_white_label",
    scaledSize: { width: 45, height: 45 },
    anchor: { x: 22.5, y: 45 },
  };

  // Destination/User location marker icon
  const destinationMarkerIcon = {
    url: "https://png.pngtree.com/png-vector/20230130/ourmid/pngtree-location-home-icon-pin-deal-clipart-png-image_6575743.png",
    scaledSize: { width: 40, height: 40 },
    anchor: { x: 20, y: 20 },
  };

  const riderLatLng = location && location.lat !== "0" ? { lat: parseFloat(location.lat), lng: parseFloat(location.logs) } : null;

  // Calculate center point between user and rider
  const center = riderLatLng && userLocation
    ? { lat: (parseFloat(riderLatLng.lat) + userLocation.lat) / 2, lng: (parseFloat(riderLatLng.lng) + userLocation.lng) / 2 }
    : riderLatLng || { lat: 28.7041, lng: 77.1025 };

  if (!isLoaded) return <div className="tracking-loading">Loading Map...</div>;

  return (
    <div className="tracking-container">
      <div className="tracking-map-wrapper">
        <GoogleMap
          zoom={15}
          center={center}
          mapContainerStyle={{ width: "100%", height: "100%" }}
        >
          {/* Display directions path */}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                polylineOptions: {
                  strokeColor: "#ff7524",
                  strokeOpacity: 0.8,
                  strokeWeight: 5,
                },
                suppressMarkers: true,
              }}
            />
          )}

          {/* User/Destination marker */}
          {userLocation && (
            <Marker
              position={userLocation}
              icon={destinationMarkerIcon}
              title="Delivery Location"
            />
          )}

          {/* Rider marker */}
          {riderLatLng && (
            <Marker
              position={riderLatLng}
              icon={riderMarkerIcon}
              title="Rider Location"
              animation={window.google?.maps?.Animation?.BOUNCE}
            />
          )}
        </GoogleMap>
      </div>

      {(riderInfo || geocodingLoading) && (
        <div className="tracking-info-panel">
          <div className="rider-header">
            <div className="rider-status">
              <span className="status-badge active">On the way</span>
            </div>
          </div>

          {geocodingLoading && (
            <div className="detail-item">
              <span>Address Geocoding:</span>
              <p>Converting address to coordinates...</p>
            </div>
          )}

          <div className="tracking-details">
            {/* Route Information */}
            {distance && duration && (
              <>
                <div className="detail-item route-info">
                  <span>📍 Distance to Delivery:</span>
                  <p className="route-text">{distance}</p>
                </div>

                <div className="detail-item route-info">
                  <span>⏱️ Estimated Time:</span>
                  <p className="route-text">{duration}</p>
                </div>
              </>
            )}

            <div className="detail-item">
              <span>Rider Location:</span>
              <p className="coord">
                {parseFloat(location.lat).toFixed(4)}, {parseFloat(location.logs).toFixed(4)}
              </p>
            </div>

            <div className="detail-item">
              <span>Latitude:</span>
              <p>{parseFloat(location.lat).toFixed(6)}</p>
            </div>

            <div className="detail-item">
              <span>Longitude:</span>
              <p>{parseFloat(location.logs).toFixed(6)}</p>
            </div>

            {userLocation && (
              <>
                <div className="detail-item">
                  <span>Delivery Address:</span>
                  <p className="address-text">
                    {userLocation.address || `${userLocation.lat.toFixed(4)}, ${userLocation.lng.toFixed(4)}`}
                  </p>
                </div>

                <div className="detail-item">
                  <span>Destination Coordinates:</span>
                  <p className="coord">
                    {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                  </p>
                </div>
              </>
            )}

            <div className="detail-item">
              <span>Last Updated:</span>
              <p>{new Date().toLocaleTimeString()}</p>
            </div>
          </div>

          <div className="tracking-actions">
            <button className="btn btn-call">📞 Call Rider</button>
            <button className="btn btn-message">💬 Message</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveTracking;
